import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Globe } from 'lucide-react';
import type { Tournament } from '@swiss-magic/shared-types';
import { playerTournamentUrl } from '@/lib/appSurface';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

function systemLabel(system: string, t: TFunction): string {
  // i18next returns the key itself on a miss; preserve backward-compat
  // by falling back to the raw system code for any we haven't translated.
  const key = `tournament_card.system.${system}`;
  const translated = t(key);
  return translated === key ? system : translated;
}

function timeControlLabel(tc: string, t: TFunction): string {
  // Reuses the form's translation keys — same labels in both surfaces.
  const slug = tc.toLowerCase().replace(/\s+/g, '_');
  const key = `tournament_form.time_control_options.${slug}`;
  const translated = t(key);
  return translated === key ? tc : translated;
}

type Props = {
  tournament: Tournament;
};

// Story 083 — the dashboard card is a clickable summary: it navigates to
// the tournament hub, which owns every lifecycle action and sub-page.
// The only inline affordance kept is the public spectator link (a quick
// share shortcut that isn't otherwise a one-click action). A draft has
// no public page yet, so the link only shows once published.
export function TournamentCard({ tournament }: Props) {
  const { t } = useTranslation('director');
  const navigate = useNavigate();
  const status = tournament.status;
  const hasPublicPage = status !== 'draft';

  return (
    <Card
      data-testid={`tournament-card-${tournament.name}`}
      onClick={() => navigate(`/tournaments/${tournament.id}`)}
      className="cursor-pointer"
    >
      <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between pb-2 gap-1">
        <CardTitle className="text-lg">{tournament.name}</CardTitle>
        <div className="flex items-center gap-2 self-start md:self-auto">
          <Badge variant="secondary" data-testid="tournament-status">
            {t(`tournament_card.status.${status}`)}
          </Badge>
          {tournament.viewerRole === 'co-director' && (
            <Badge variant="outline" data-testid="co-director-badge">
              {t('tournament_card.co_director_badge')}
            </Badge>
          )}
          {tournament.godModeUsed && (
            <Badge
              variant="destructive"
              data-testid="god-mode-used-badge"
              title={t('tournament_card.god_mode_title')}
            >
              {t('tournament_card.god_mode_badge')}
            </Badge>
          )}
          {tournament.system && tournament.system !== 'swiss' && (
            <span className="text-xs text-muted-foreground" data-testid="tournament-system">
              {systemLabel(tournament.system, t)}
            </span>
          )}
          {tournament.gameName && tournament.gameName !== 'Chess' && (
            <span className="text-xs text-muted-foreground" data-testid="tournament-game">
              {tournament.gameName}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {tournament.startDate && <span>{tournament.startDate}</span>}
          {tournament.location && <span>{tournament.location}</span>}
          {tournament.timeControl && <span>{timeControlLabel(tournament.timeControl, t)}</span>}
          {tournament.matchLength && (
            <span>{t('tournament_card.match_length', { length: tournament.matchLength })}</span>
          )}
          {tournament.rounds && (
            <span>{t('tournament_card.rounds_count', { count: tournament.rounds })}</span>
          )}
        </div>
        {hasPublicPage && (
          <div className="mt-3">
            <Button asChild variant="outline" size="sm">
              <a
                data-testid="public-tournament-link"
                href={playerTournamentUrl(tournament.slug)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <Globe className="mr-1 h-3 w-3" />
                {t('tournament_card.buttons.public_link')}
              </a>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
